import React, { useContext, useEffect, useState } from 'react';
import { Button, H5, Body, Stack, Link } from '@qred/components-library';
import useTranslate from '~/hooks/useTranslate';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { StepStatus } from '~/enums';
import { useAuth } from '~/contexts/AuthContext/AuthProvider';
import LoanAmount from '~/components/shared/LoanAmount/LoanAmount';
import { RootState } from '~/store';
import { loanAmountRounderHelper } from '~/helpers/loanAmountRounder.helper';
import {
  formatAmount,
  formatAmountNumbersOnly,
  formatAmountWithoutCurrency,
  removeNonDigits,
} from '~/helpers/formatters.helper';
import { FormStatus, ValidationContext } from '~/components/hoc/withValidation';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { onChangeInputEvent } from '~/types/types';
import { LoanAmountDefaultValues, policyTermsUrl } from '~/constants/markets';
import {
  updateCurrentStepNumber,
  updateForm,
} from '~/store/slices/loanApplication.slice';
import {
  FormBottomContent,
  FormTopContent,
  LaunchViewFormContainer,
  LaunchViewFormContent,
  LoanAmountInputWrapper,
} from './LaunchViewForm.styled';

const LaunchViewForm = () => {
  const t = useTranslate();
  const dispatch = useAppDispatch();

  const [localAmount, setLocalAmount] = useState('');

  const validationContext = useContext(ValidationContext);

  const { isAuthenticated } = useAuth();

  const {
    loanApplication: { form },
    intl: { market, language },
  } = useSelector((state: RootState) => state);

  const { amount } = form;

  useEffect(() => {
    if (amount) {
      setLocalAmount(amount);
    }
  }, [amount]);

  const handleOnBlur = (event: onChangeInputEvent) => {
    const { value } = event.target;
    if (value) {
      validationContext.removePropertyFromValidationErrors('LoanAmount');
      const roundedAmount = loanAmountRounderHelper(value, market);
      const formattedValue = formatAmountWithoutCurrency(
        removeNonDigits(roundedAmount)
      );
      setLocalAmount(formattedValue);
    }
    dispatch(
      pushToGtmLoanAction({
        stepActionNumber: 0.1,
        actionName: 'loan_amount_change',
      })
    );
  };

  const handleOnChange = (event: onChangeInputEvent) => {
    const { value } = event.target;
    setLocalAmount(value ? formatAmountNumbersOnly(value) : value);
  };

  const handleStartFlowClick = () => {
    validationContext.setFormStatus(FormStatus.SUBMITTING);
    if (!validationContext.isFormValid) {
      validationContext.setFormStatus(FormStatus.SUBMITTED);
      return;
    }

    dispatch(updateForm({ amount: localAmount }));

    dispatch(
      pushToGtmLoanAction({
        stepActionNumber: 0.2,
        actionName: isAuthenticated
          ? 'next_button_pressed'
          : 'start_manual_flow_pressed',
      })
    );
    dispatch(
      pushToGtmLoanAction({
        stepStatus: StepStatus.Finished,
      })
    );
    dispatch(updateCurrentStepNumber(1));
  };

  return (
    <>
      <LaunchViewFormContainer>
        <LaunchViewFormContent justify="center" align="center">
          <FormTopContent align="center">
            <H5 align="center" dataCy="launchview_page_header">
              {t('Onboarding.LaunchViewLoanTitle')}
            </H5>
            <LoanAmountInputWrapper>
              <LoanAmount
                value={localAmount}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
                name="LoanAmount"
                dataCy="launchview_loan_amount"
              />
              <Body align="center" size="sm">
                {t('Onboarding.LaunchViewLoanApplyMax', {
                  min: formatAmount(LoanAmountDefaultValues[market].min),
                  max: formatAmount(LoanAmountDefaultValues[market].max),
                })}
              </Body>
            </LoanAmountInputWrapper>
          </FormTopContent>
          <FormBottomContent>
            <Stack spacing="xxl" align="center">
              {isAuthenticated ? (
                <Button
                  variant="primary"
                  onClick={handleStartFlowClick}
                  dataCy="launchview_authenticated_next_button"
                  fullWidth
                  size="md"
                >
                  {t('Onboarding.Next')}
                </Button>
              ) : (
                <Button
                  variant={'primary'}
                  fullWidth
                  onClick={handleStartFlowClick}
                  dataCy="launchview_apply_without_auth"
                  size="md"
                >
                  {t('Onboarding.LaunchViewLoanManualFlow')}
                </Button>
              )}
            </Stack>
            <Body align="center" color="textDark.200" size="sm">
              {t('TermsAndConfirmationCard.PolicyAgreementText', {
                policyAgreementLink: (
                  <a
                    href={policyTermsUrl[market][language]}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Link size="sm">
                      {t('TermsAndConfirmationCard.PolicyAgreementLink')}
                    </Link>
                  </a>
                ),
              })}
            </Body>
          </FormBottomContent>
        </LaunchViewFormContent>
      </LaunchViewFormContainer>
    </>
  );
};

export default LaunchViewForm;
